// ── Mapa canónico de destinos ────────────────────
// Normaliza cualquier variante escrita hacia un nombre canónico
const MAPA_CANONICO = {
  // Brasil
  "rio de janeiro":      "Rio de Janeiro",
  "rio":                 "Rio de Janeiro",
  "buzios":              "Buzios",
  "buzios":              "Buzios",
  "arraial do cabo":     "Arraial do Cabo",
  "arraial":             "Arraial do Cabo",
  "arraial d ajuda":     "Arraial D'Ajuda",
  "arraial d'ajuda":     "Arraial D'Ajuda",
  "arraial da ajuda":    "Arraial D'Ajuda",
  "cabo frio":           "Cabo Frio",
  "cabo":                "Cabo Frio",
  "porto de galinhas":   "Porto de Galinhas",
  "porto galinhas":      "Porto de Galinhas",
  "maragogi":            "Maragogi",
  "maceio":              "Maceio",
  "natal":               "Natal",
  "porto seguro":        "Porto Seguro",
  "florianopolis":       "Florianopolis",
  "camboriu":            "Camboriu",
  "bombas y bombinhas":  "Bombas y Bombinhas",
  "barra de tijuca":     "Barra de Tijuca",
  "pipa":                "Pipa",
  // Caribe
  "punta cana":          "Punta Cana",
  "bayahibe":            "Bayahibe",
  "puerto plata":        "Puerto Plata",
  "cancun":              "Cancun",
  "playa del carmen":    "Playa del Carmen",
  "cartagena":           "Cartagena",
  "panama":              "Panama",
  // Argentina
  "bariloche":           "Bariloche",
  "ushuaia":             "Ushuaia",
}

// Destinos que NO tienen medalla (no aparecen en conquistas)
const SIN_MEDALLA = ["ushuaia", "bariloche"]

/**
 * Toma un string de destino (puede ser simple o combinado)
 * Devuelve { display, medallas[] }
 * 
 * Ej: "Rio + Buzios" → { display: "Rio + Buzios", medallas: ["Rio de Janeiro", "Buzios"] }
 * Ej: "NATAL"        → { display: "Natal",        medallas: ["Natal"] }
 */
export function normalizarDestino(raw) {
  if (!raw) return { display: "—", medallas: [] }

  // Separar por +, -, / o coma (pero no las que están dentro de nombres conocidos)
  // Usamos regex para dividir por separadores comunes
  const partes = raw
    .split(/\s*[+\-\/,]\s*/)
    .map(p => p.trim())
    .filter(p => p.length > 0)

  const medallas = []
  const displayPartes = []

  partes.forEach(parte => {
    const clave = parte.toLowerCase().trim()
    const canonico = MAPA_CANONICO[clave] || capitalizar(parte)

    displayPartes.push(canonico)

    if (!SIN_MEDALLA.includes(clave) && !medallas.includes(canonico)) {
      medallas.push(canonico)
    }
  })

  return {
    display:  displayPartes.join(" + "),
    medallas: medallas,
  }
}

function capitalizar(str) {
  return str
    .toLowerCase()
    .split(" ")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

/**
 * Toma el array de viajes de un cliente
 * Devuelve un Set con todos los destinos (nombres canónicos) visitados
 * Útil para el Mapa de Conquistas
 */
export function obtenerMedallasCliente(viajes) {
  const medallas = new Set()
  if (!viajes) return medallas

  viajes.forEach(v => {
    if (!v.destino) return
    const { medallas: m } = normalizarDestino(v.destino)
    m.forEach(med => medallas.add(med))
  })

  return medallas
}

/**
 * Parsea el CSV de viajes de Sheets
 * Devuelve array de objetos listos para Firestore
 */
export function parsearCSVViajes(texto) {
  const lineas = texto.trim().split("\n").filter(l => l.trim())
  if (lineas.length < 2) return []

  // Normalizar encabezados
  const encabezados = lineas[0].split(",").map(h =>
    h.trim().toLowerCase()
      .replace(/[áàä]/g, "a").replace(/[éèë]/g, "e")
      .replace(/[íìï]/g, "i").replace(/[óòö]/g, "o")
      .replace(/[úùü]/g, "u").replace(/[ñ]/g, "n")
      .replace(/[^a-z0-9\s]/g, "").trim()
  )

  const filas = []
  for (let i = 1; i < lineas.length; i++) {
    // Parseo robusto que maneja comillas
    const valores = parsearLineaCSV(lineas[i])
    const fila = {}
    encabezados.forEach((h, idx) => { fila[h] = (valores[idx] || "").trim() })

    // Necesita ID de cliente y destino mínimamente
    const idCliente = fila["id cliente"] || fila["idcliente"] || fila["id_cliente"]
    const destino   = fila["destino"]
    if (!idCliente || !destino) continue

    const { display, medallas } = normalizarDestino(destino)

    const num = (v) => {
      const n = parseFloat((v || "0").replace(/[$,]/g, "").replace(",", "."))
      return isNaN(n) ? 0 : n
    }

    filas.push({
      id_viaje:          fila["id"] || fila["id viaje"] || "",
      id_cliente:        idCliente.toString(),
      destino:           display,
      destino_medallas:  medallas,
      fecha_reserva:     fila["fecha de reserva"] || "",
      fecha_salida:      normalizarFecha(fila["fecha de viaje"] || ""),
      fecha_regreso:     normalizarFecha(fila["fecha de vuelta"] || ""),
      noches:            num(fila["cant noches"]),
      pax:               num(fila["pax"]),
      aerolinea:         capitalizar(fila["aerolinea"] || fila["aerolinea"] || ""),
      asistencia:        fila["asistencia al viajero"] || fila["asistencia"] || "No",
      hotel1:            fila["hotel 1"] || "",
      hotel2:            fila["hotel 2"] || "",
      incluye:           fila["incluye"] || "",
      estado:            normalizarEstadoViaje(fila["estado del viaje"] || ""),
      // Solo admin
      canal_venta:       fila["canal de venta"] || "",
      referido_por:      fila["referido por"] || "",
      origen:            fila["origen"] || "",
      grupo_wspp:        fila["grupo"] || fila["grupotelfono"] || fila["grupo"] || "No",
      monto_venta:       num(fila["monto de venta "] || fila["monto de venta"] || "0"),
      nombre_csv:        fila["nombre y apellido"] || "",
    })
  }

  return filas
}

function parsearLineaCSV(linea) {
  const resultado = []
  let actual = ""
  let enComillas = false

  for (let i = 0; i < linea.length; i++) {
    const c = linea[i]
    if (c === '"') {
      enComillas = !enComillas
    } else if (c === "," && !enComillas) {
      resultado.push(actual)
      actual = ""
    } else {
      actual += c
    }
  }
  resultado.push(actual)
  return resultado
}

function normalizarFecha(str) {
  if (!str) return ""
  // Convierte DD/MM/YYYY a YYYY-MM-DD
  const partes = str.split("/")
  if (partes.length === 3) {
    const [dia, mes, anio] = partes
    return anio + "-" + mes.padStart(2, "0") + "-" + dia.padStart(2, "0")
  }
  return str
}

function normalizarEstadoViaje(estado) {
  const mapa = {
    "pendiente":   "confirmado",
    "confirmado":  "confirmado",
    "en curso":    "en_curso",
    "completado":  "completado",
    "cancelado":   "cancelado",
    "viajando":    "en_curso",
  }
  return mapa[estado.toLowerCase()] || "confirmado"
}